import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import UndoIcon from "@mui/icons-material/Undo";
import { IconButtonProps, InputBaseComponentProps, OutlinedInputProps } from "@mui/material";
import { green } from "@mui/material/colors";
import IconButton from "@mui/material/IconButton";
import Input from "@mui/material/Input";
import { alpha, styled } from "@mui/material/styles";
import TextField, { OutlinedTextFieldProps } from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { DatePicker, DatePickerProps, PickersDay, PickersDayProps, PickersInputProps } from "@mui/x-date-pickers";
import { PickerValue } from "@mui/x-date-pickers/internals";
import AHDate from "_shared/dates/date";
import { diffLines, diffWords } from "diff";
import { enqueueSnackbar } from "notistack";
import React, { forwardRef, useCallback, useEffect } from "react";
import CommentsButton from "work/proposals/comments/_view/comments-button";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import { ProposalIssue } from "work/proposals/issues/proposal-issues";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import TextChange, { TextChangeCollection } from "work/proposals/redlining/__diff/text-change";
import { ChangeType } from "work/proposals/redlining/__diff/view/diff-changes";
import DiffText from "work/proposals/redlining/__diff/view/diff-text";
import FieldRedline from "work/proposals/redlining/field-redlines/field-redline";

const DiffTextContainer = styled("div")(({ theme }) => ({
  flexDirection: "column",
  height: "100%",
  paddingRight: theme.spacing(2),
  width: "100%",
}));
const DiffInputContainer = styled("div")(() => ({
  width: "100%",
  "&.Mui-disabled": {
    webkitTextFillColor: "revert !important",
    opacity: "0.75 !important",
  },
}));
const DiffInputSpan = styled("span")(() => ({
  "&.MuiInputBase-input": {
    flexDirection: "column",
  },
}));
const ResolvedDateValue = styled("span")(() => ({
  color: "inherit",
  fontWeight: "inherit",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
}));
const RedlineDatePicker = styled(DatePicker, {
  shouldForwardProp: (props) => props !== "traversable",
})<{ traversable?: boolean }>(() => ({
  minWidth: "20rem",
  width: "min-content",
}));
const CalendarDay = styled(PickersDay, {
  shouldForwardProp: (prop) => !["resolved", "changeType", "acceptedResolved"].includes(prop.toString()),
})<{ changeType?: ChangeType; resolved: boolean; acceptedResolved: boolean }>(({ changeType, resolved, acceptedResolved, theme }) => ({
  "&.MuiPickersDay-root": {
    "&:disabled": {
      color: (function (): string {
        if (changeType === ChangeType.removed && acceptedResolved) {
          return theme.palette.text.disabled;
        }
        if (changeType === ChangeType.added || changeType === ChangeType.removed) {
          return theme.palette.text.primary;
        }
        return "revert";
      })(),
    },
    backgroundColor: (function (): string {
      if (changeType === ChangeType.added && acceptedResolved) return "transparent";
      if (changeType === ChangeType.added && resolved) return theme.palette.primary.main;
      if (changeType === ChangeType.added) return green[300];
      return "unset";
    })(),
    border: (function (): string {
      if (changeType === ChangeType.removed) {
        let borderColor: string;
        if (acceptedResolved) {
          borderColor = theme.palette.text.primary;
        } else if (resolved) {
          borderColor = theme.palette.primary.main;
        } else {
          borderColor = theme.palette.error.main;
        }
        return `2px solid ${borderColor}`;
      }
      return "unset";
    })(),
    fontWeight: (function (): number | string {
      if (changeType === ChangeType.added && acceptedResolved) return 500;
      return "inherit";
    })(),
    fontSynthesis: (function (): string | undefined {
      if (changeType === ChangeType.added && acceptedResolved) return "weight";
      return undefined;
    })(),
    textDecoration: (function (): string {
      if (changeType === ChangeType.removed) return "line-through";
      return "unset";
    })(),
    textDecorationColor: (function (): string {
      if (changeType === ChangeType.removed) {
        let color: string;
        if (acceptedResolved) {
          color = theme.palette.text.primary;
        } else if (resolved) {
          color = theme.palette.primary.main;
        } else {
          color = theme.palette.error.main;
        }
        return color;
      }
      return "unset";
    })(),
    textDecorationThickness: (function (): string {
      if (changeType === ChangeType.removed) return "2px";
      return "unset";
    })(),
    "&.Mui-selected": {
      backgroundColor: (function (): string {
        if (changeType === ChangeType.added && acceptedResolved) return "transparent";
        if (changeType === ChangeType.added && resolved) return theme.palette.primary.main;
        if (changeType === ChangeType.added) return green[300];
        return "unset";
      })(),
      color: (function (): string {
        if (changeType === ChangeType.removed && acceptedResolved) return theme.palette.text.disabled;
        return theme.palette.text.primary;
      })(),
    },
    "&.Mui-selected:hover": {
      backgroundColor: (function (): string {
        if (changeType === ChangeType.added && acceptedResolved) return "transparent";
        if (changeType === ChangeType.added && resolved) return theme.palette.primary.main;
        if (changeType === ChangeType.added) return green[300];
        return "unset";
      })(),
    },
  },
}));
const ActionButton = styled(IconButton)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(0.5),
}));

interface RedlinePickersDayProps extends PickersDayProps {
  originalDate: AHDate | null;
  revisedDate: AHDate | null;
  currentDate: AHDate | null | undefined;
  resolved: boolean;
  acceptedResolved: boolean;
}

export function getCalendarDayChangeType(params: {
  day: AHDate;
  originalDate: AHDate | null;
  revisedDate: AHDate | null;
  currentDate: AHDate | null | undefined;
}): ChangeType {
  const { day, originalDate, revisedDate, currentDate } = params;

  let previousDate: AHDate | null = null;
  let nextDate: AHDate | null = null;

  if ((currentDate === null && revisedDate === null) || currentDate?.isEqualTo(revisedDate)) {
    previousDate = originalDate;
    nextDate = currentDate ?? null;
  } else if (currentDate || currentDate === null) {
    previousDate = revisedDate;
    nextDate = currentDate;
  } else {
    previousDate = originalDate;
    nextDate = revisedDate;
  }

  const matchesPrevious = previousDate?.isSame(day, "day") ?? false;
  const matchesNext = nextDate?.isSame(day, "day") ?? false;

  if (matchesPrevious && matchesNext) return ChangeType.unchanged;
  if (matchesNext) return ChangeType.added;
  if (matchesPrevious) return ChangeType.removed;
  return ChangeType.unchanged;
}

export function getDisplayedDateValue(dateRedline: FieldRedline<AHDate>) {
  return dateRedline.currentEntry?.asMoment ?? dateRedline.revisedEntry?.asMoment ?? dateRedline.originalEntry?.asMoment;
}

function RevisedDay(props: Readonly<RedlinePickersDayProps>) {
  const { originalDate, revisedDate, currentDate, resolved, acceptedResolved, ...otherProps } = props;

  let changeType = ChangeType.unchanged;

  if (!otherProps.outsideCurrentMonth) {
    changeType = getCalendarDayChangeType({
      day: AHDate.fromMoment(otherProps.day),
      originalDate,
      revisedDate,
      currentDate,
    });
  }

  return (
    <CalendarDay
      {...otherProps}
      disabled={otherProps.disabled || changeType !== ChangeType.unchanged}
      selected={otherProps.selected || changeType !== ChangeType.unchanged}
      changeType={changeType}
      resolved={Boolean(resolved)}
      acceptedResolved={acceptedResolved}
    />
  );
}

interface DatePickerTextFieldProps extends OutlinedTextFieldProps {
  InputProps?: Partial<OutlinedInputProps & PickersInputProps>;
  dateRedline: FieldRedline<AHDate>;
  focused: boolean;
  animateAcceptedResolved?: boolean;
  onTraverseIn?: () => void;
}

const DatePickerTextField: React.FC<DatePickerTextFieldProps> = (props) => {
  const { dateRedline, focused, InputProps, slotProps, onTraverseIn, animateAcceptedResolved, ...otherProps } = props;
  const hasAcceptedResolvedChange = dateRedline.isResolved && dateRedline.isAccepted && dateRedline.wasRevised;
  const shouldShrinkLabel =
    dateRedline.currentEntry !== undefined &&
    dateRedline.currentEntry !== null ||
    dateRedline.originalEntry !== null ||
    dateRedline.revisedEntry !== null;
  const resolvedHelperText = hasAcceptedResolvedChange ? "Resolved" : otherProps.helperText;

  return (
    <TextField
      {...otherProps}
      label={dateRedline.label}
      helperText={resolvedHelperText}
      focused={focused}
      sx={{
        ...(hasAcceptedResolvedChange && {
          "& .MuiInputLabel-root": {
            color: green[700],
          },
          "& .MuiInputLabel-root.Mui-focused": {
            color: green[700],
          },
          "& .MuiOutlinedInput-root": {
            backgroundColor: alpha(green[500], 0.06),
          },
          "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
            borderColor: green[500],
          },
          "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: green[600],
          },
          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: green[700],
          },
          "& .MuiFormHelperText-root": {
            color: green[700],
          },
        }),
      }}
      slotProps={{
        ...slotProps,
        input: {
          ...slotProps?.input,
          ...InputProps,
          inputComponent: DiffDateInputComponent,
          inputProps: {
            dateRedline,
            animateAcceptedResolved,
            onTraverseIn,
          },
        },
        inputLabel: {
          ...slotProps?.inputLabel,
          shrink: shouldShrinkLabel,
        },
      }}
    />
  );
};

interface DiffDateInputComponentProps extends InputBaseComponentProps {
  dateRedline: FieldRedline<AHDate>;
  animateAcceptedResolved?: boolean;
  onTraverseIn?: () => void;
}

const DiffDateInputComponent = forwardRef<HTMLInputElement, DiffDateInputComponentProps>(
  function DiffDateInputComponent(props, forwardedRef) {
    const { className, onTraverseIn, dateRedline, animateAcceptedResolved, ...otherProps } = props;
    const hasAcceptedResolvedChange = dateRedline.isResolved && dateRedline.isAccepted && dateRedline.wasRevised;
    const isAcceptedResolvedRemoval = hasAcceptedResolvedChange && dateRedline.currentEntry === null;
    const showAcceptedResolvedAnimation = Boolean(
      animateAcceptedResolved &&
      hasAcceptedResolvedChange &&
      !isAcceptedResolvedRemoval
    );
    const showResolvedDiff = !hasAcceptedResolvedChange || showAcceptedResolvedAnimation || isAcceptedResolvedRemoval;

    const getTextChanges = useCallback(() => {
      const collection = new TextChangeCollection();
      let previous = "";
      let next = "";
      if (isAcceptedResolvedRemoval) {
        previous = dateRedline.originalEntry?.asFormattedString ?? dateRedline.revisedEntry?.asFormattedString ?? "";
        next = "";
      } else if (dateRedline.currentEntry === undefined) {
        previous = dateRedline.originalEntry?.asFormattedString ?? "";
        next = dateRedline.revisedEntry?.asFormattedString ?? "";
      } else {
        previous = dateRedline.revisedEntry?.asFormattedString ?? "";
        next = dateRedline.currentEntry ? dateRedline.currentEntry.asFormattedString : "";
      }
      const diff = dateRedline.diffWords ? diffWords(previous, next) : diffLines(previous, next);
      return diff.map((change) => new TextChange({ diff: change, parent: collection }));
    }, [dateRedline, isAcceptedResolvedRemoval]);

    return (
      <DiffTextContainer>
        <DiffInputContainer className={className}>
          <DiffInputSpan>
            {showResolvedDiff ? (
              <DiffText
                textChanges={getTextChanges()}
                allResolved={dateRedline.isResolved}
                acceptedResolved={dateRedline.isAccepted && dateRedline.wasRevised}
                animateAcceptedResolved={animateAcceptedResolved}
                disablePopover={true}
                onClick={onTraverseIn}
              />
            ) : (
              <ResolvedDateValue onClick={onTraverseIn}>
                {dateRedline.currentEntry?.asFormattedString ?? dateRedline.revisedEntry?.asFormattedString ?? dateRedline.originalEntry?.asFormattedString ?? ""}
              </ResolvedDateValue>
            )}
          </DiffInputSpan>
        </DiffInputContainer>
        <Input {...otherProps} inputRef={forwardedRef} style={{ position: "absolute", height: 0, width: 0 }} />
      </DiffTextContainer>
    );
  },
);

export interface RedlineDateInputProps extends DatePickerProps {
  dateRedline: FieldRedline<AHDate>;
  issues?: ProposalIssue[];
  focused: boolean;
  hideUndoButton?: boolean;
  commentThreads: CommentThread[];
  onCommentsClicked?: () => void;
  onDateRedlineChange: (newRedline: FieldRedline<AHDate>, traversalFieldOverride?: ProposalField) => void;
  onTraverseIn?: () => void;
}

const DateRedlinePicker = (props: Readonly<RedlineDateInputProps>) => {
  const {
    dateRedline,
    focused,
    disabled,
    hideUndoButton,
    commentThreads,
    onCommentsClicked,
    onDateRedlineChange,
    onTraverseIn,
    ...otherProps
  } = props;
  const [animateAcceptedResolved, setAnimateAcceptedResolved] = React.useState(false);
  const acceptedResolvedAnimationTimeoutRef = React.useRef<ReturnType<typeof globalThis.setTimeout> | undefined>(undefined);

  useEffect(() => {
    return () => {
      if (acceptedResolvedAnimationTimeoutRef.current !== undefined) {
        globalThis.clearTimeout(acceptedResolvedAnimationTimeoutRef.current);
      }
    };
  }, []);

  const triggerAcceptedResolvedAnimation = useCallback(() => {
    setAnimateAcceptedResolved(true);
    if (acceptedResolvedAnimationTimeoutRef.current !== undefined) {
      globalThis.clearTimeout(acceptedResolvedAnimationTimeoutRef.current);
    }
    acceptedResolvedAnimationTimeoutRef.current = globalThis.setTimeout(() => {
      setAnimateAcceptedResolved(false);
      acceptedResolvedAnimationTimeoutRef.current = undefined;
    }, 1000);
  }, []);

  function handleChangeAccepted() {
    try {
      triggerAcceptedResolvedAnimation();
      onDateRedlineChange(dateRedline.accept());
    } catch (error) {
      console.error("Error accepting change.", error);
      enqueueSnackbar("Error accepting change.", { variant: "error" });
    }
  }

  function handleChangeRejected() {
    try {
      onDateRedlineChange(dateRedline.reject());
    } catch (error) {
      console.error("Error rejecting change.", error);
      enqueueSnackbar("Error rejecting change.", { variant: "error" });
    }
  }

  function handleChangeEdited(newDate: AHDate | null) {
    try {
      if (!dateRedline.currentEntry && !dateRedline.revisedEntry && newDate) {
        onDateRedlineChange(dateRedline.add(newDate), dateRedline.field);
        return;
      }
      onDateRedlineChange(dateRedline.edit(newDate), dateRedline.field);
    } catch (error) {
      console.error("Error editing change.", error);
      enqueueSnackbar("Error editing change.", { variant: "error" });
    }
  }

  function handleRemoveClicked() {
    try {
      onDateRedlineChange(dateRedline.remove(), dateRedline.field);
    } catch (error) {
      console.error("Error removing change.", error);
      enqueueSnackbar("Error removing change.", { variant: "error" });
    }
  }

  function handleUndoChanges() {
    try {
      onDateRedlineChange(dateRedline.undo(), dateRedline.field);
    } catch (error) {
      console.error("Error undoing changes.", error);
      enqueueSnackbar("Error undoing changes.", { variant: "error" });
    }
  }

  const ActionButtons = (buttonProps: IconButtonProps) => {
    return (
      <>
        {!disabled && (
          <>
            {!dateRedline.isResolved && (
              <>
                <Tooltip title="Accept Change">
                  <span>
                    <ActionButton onClick={() => handleChangeAccepted()}>
                      <CheckIcon color="success" />
                    </ActionButton>
                  </span>
                </Tooltip>
                <Tooltip title="Reject Change">
                  <span>
                    <ActionButton onClick={() => handleChangeRejected()}>
                      <CloseIcon color="error" />
                    </ActionButton>
                  </span>
                </Tooltip>
              </>
            )}
            {dateRedline.isResolved && dateRedline.currentEntry && (
              <Tooltip title="Remove">
                <span>
                  <ActionButton
                    onClick={() => {
                      handleRemoveClicked();
                    }}
                  >
                    <CloseIcon />
                  </ActionButton>
                </span>
              </Tooltip>
            )}
            {dateRedline.canBeUndone && !hideUndoButton && (
              <Tooltip title="Undo Changes">
                <span>
                  <ActionButton onClick={handleUndoChanges}>
                    <UndoIcon color="secondary" />
                  </ActionButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Edit">
              <span>
                <ActionButton
                  {...buttonProps}
                  onClick={(event) => {
                    onTraverseIn?.();
                    buttonProps.onClick?.(event);
                  }}
                />
              </span>
            </Tooltip>
          </>
        )}
        <CommentsButton
          commentThreads={commentThreads}
          toolTip="Comments"
          field={dateRedline.field}
          onCommentsClicked={() => {
            onCommentsClicked?.();
            onTraverseIn?.();
          }}
        />
      </>
    );
  };

  return (
    <RedlineDatePicker
      {...otherProps}
      enableAccessibleFieldDOMStructure={false} // TODO: Re-enable when date picker text input field is properly implemented
      disablePast
      disabled={disabled}
      value={getDisplayedDateValue(dateRedline) ?? null}
      traversable={Boolean(onTraverseIn)}
      referenceDate={getDisplayedDateValue(dateRedline)}
      onChange={(date: PickerValue) => {
        const newDate = date?.endOf("day") ? AHDate.fromMoment(date) : null;
        handleChangeEdited(newDate);
      }}
      slots={{
        openPickerIcon: EditCalendarIcon,
        openPickerButton: ActionButtons,
        day: (dayProps) => (
          <RevisedDay
            {...dayProps}
            originalDate={dateRedline.originalEntry}
            revisedDate={dateRedline.revisedEntry}
            currentDate={dateRedline.currentEntry}
            resolved={dateRedline.isResolved}
            acceptedResolved={dateRedline.isAccepted && dateRedline.wasRevised}
          />
        ),
        textField: DatePickerTextField,
      }}
      slotProps={{
        openPickerIcon: {
          color: "primary",
        },
        day: {
          originalDate: dateRedline.originalEntry,
          revisedDate: dateRedline.revisedEntry,
          currentDate: dateRedline.currentEntry,
          resolved: dateRedline.isResolved,
          acceptedResolved: dateRedline.isAccepted && dateRedline.wasRevised,
        } as RedlinePickersDayProps,
        textField: {
          error: Boolean(props.issues?.length),
          helperText: props.issues?.map((issue) => issue.description).join("\n"),
          variant: "outlined",
          margin: "normal",
          dateRedline: dateRedline,
          focused: focused,
          animateAcceptedResolved,
          slotProps: {
            inputLabel: {
              shrink:
                (dateRedline.currentEntry !== undefined &&
                dateRedline.currentEntry !== null) ||
                dateRedline.originalEntry !== null ||
                dateRedline.revisedEntry !== null,
            },
          },
          onFocus: onTraverseIn,
          onClick: onTraverseIn,
        } as DatePickerTextFieldProps,
      }}
    />
  );
};

export default DateRedlinePicker;
